/**
 * Archive and restore calls, the same for all five kinds. Both are idempotent
 * on the server, so a double click or a retry is safe. Either one refetches the
 * kind's lists and detail, and whatever shows it as history (an experiment's
 * dataset and evaluators, a queue's scores).
 */

import { useQueryClient, type QueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useCallback } from "react";
import { errorText } from "@/components/ui/states";
import { api } from "@/lib/api";
import { invalidatePaths, useApiMutation, useApiQuery } from "@/lib/query";
import { useUrlState } from "@/lib/use-url-state";
import { useUiStore } from "@/stores/ui-store";
import { KINDS, type ArchiveKind } from "./kinds";

const affected = (kind: ArchiveKind) => {
  const { path } = KINDS[kind];
  if (kind === "dataset" || kind === "evaluator") return [path, "/experiments"];
  if (kind === "scoreConfig") return [path, "/annotation-queues"];
  return [path];
};

async function call(client: QueryClient, kind: ArchiveKind, id: string, verb: "archive" | "restore") {
  await api.post(`${KINDS[kind].path}/${id}/${verb}`);
  await invalidatePaths(client, affected(kind));
}

export const useArchive = (kind: ArchiveKind) =>
  useApiMutation<unknown, string>("post", (id) => `${KINDS[kind].path}/${id}/archive`, {
    invalidate: affected(kind),
  });

/** The archived rows only, for the Archived tab and its count. */
export const useArchivedList = <T>(kind: ArchiveKind, enabled = true) =>
  useApiQuery<T[]>(KINDS[kind].path, { archived: true }, { enabled });

/**
 * `restore({id, name})`: no confirm (it hides nothing and loses nothing), a
 * toast after. `undo` is the same call from the archive toast's Undo, which
 * may run after the dialog that archived it has gone, so it is not tied to a
 * mounted mutation.
 */
export function useRestore(kind: ArchiveKind) {
  const client = useQueryClient();
  const notify = useUiStore((s) => s.notify);
  return useCallback(
    (target: { id: string; name: string }, undo = false) =>
      call(client, kind, target.id, "restore").then(
        () => notify(undo ? `"${target.name}" is back.` : `Restored "${target.name}".`),
        (err: unknown) => notify(errorText(err, `Could not restore "${target.name}". Try again.`), "error"),
      ),
    [client, kind, notify],
  );
}

export type ArchiveView = "active" | "archived";

/** The list's tab, from `?view=`; anything else is Active. */
export function useArchiveView() {
  const url = useUrlState();
  const view: ArchiveView = url.get("view") === "archived" ? "archived" : "active";
  // Back to page one: the other tab has other rows.
  const setView = (next: ArchiveView) => url.set({ view: next === "archived" ? next : null, page: null });
  return [view, setView] as const;
}

/**
 * A list page's rows for its tab: the active list it already loads, or the
 * archived one. Both load, so both tabs show a count.
 */
export function useArchiveTabs<T>(kind: ArchiveKind, active: UseQueryResult<T[]>) {
  const [view] = useArchiveView();
  const archived = useArchivedList<T>(kind);
  return {
    view,
    query: view === "archived" ? archived : active,
    counts: { active: active.data?.length, archived: archived.data?.length },
  };
}
